import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { buildApiUrl } from "../config/api";
import "./Account.css";

export default function MyDownloads() {
  const { user, isLoggedIn, authLoading } = useAuth();

  const [downloads, setDownloads] = useState([]);
  const [downloadsLoading, setDownloadsLoading] = useState(true);
  const [downloadsError, setDownloadsError] = useState("");

  useEffect(() => {
    document.title = "My Downloads | MayaChitro";
  }, []);

  useEffect(() => {
    let ignore = false;

    async function fetchDownloads() {
      if (!isLoggedIn) {
        setDownloadsLoading(false);
        return;
      }

      try {
        setDownloadsLoading(true);
        setDownloadsError("");

        const response = await fetch(buildApiUrl("/downloads/my-downloads"), {
          method: "GET",
          credentials: "include",
          headers: {
            Accept: "application/json",
          },
        });

        const data = await response.json();

        if (!ignore && response.ok && data.success) {
          setDownloads(data.downloads || data.items || []);
        } else if (!ignore) {
          setDownloads([]);
          setDownloadsError(
            data.message ||
              "Download access backend route is not connected yet. This page is ready for integration."
          );
        }
      } catch (error) {
        console.error("My downloads fetch error:", error);

        if (!ignore) {
          setDownloads([]);
          setDownloadsError(
            "Download access backend route is not connected yet. After backend setup, purchased files will appear here."
          );
        }
      } finally {
        if (!ignore) {
          setDownloadsLoading(false);
        }
      }
    }

    fetchDownloads();

    return () => {
      ignore = true;
    };
  }, [isLoggedIn]);

  if (authLoading) {
    return (
      <main className="account-page">
        <div className="account-loading-card">
          <p className="account-eyebrow">MayaChitro Downloads</p>
          <h1>Checking your account...</h1>
          <p>Please wait a moment.</p>
        </div>
      </main>
    );
  }

  if (!isLoggedIn) {
    return <Navigate to="/login?redirect=/my-downloads" replace />;
  }

  return (
    <main className="account-page">
      <section className="account-hero">
        <div>
          <p className="account-eyebrow">Digital Access</p>
          <h1>My Downloads</h1>
          <p>
            Access purchased photography files, printable wall art, and licensed
            digital downloads from your MayaChitro account.
          </p>
        </div>

        <Link to="/my-orders" className="account-logout-btn">
          View Orders
        </Link>
      </section>

      <section className="account-dashboard-grid">
        <Link to="/account" className="account-stat-card">
          <span>Account</span>
          <strong>{user?.fullName || "Customer"}</strong>
          <small>Manage your profile and email.</small>
        </Link>

        <Link to="/wishlist" className="account-stat-card">
          <span>Wishlist</span>
          <strong>Saved Items</strong>
          <small>Review visuals before buying.</small>
        </Link>

        <Link to="/my-orders" className="account-stat-card">
          <span>Orders</span>
          <strong>Purchase History</strong>
          <small>View previous orders and licenses.</small>
        </Link>

        <Link to="/shop" className="account-stat-card highlighted">
          <span>Shop</span>
          <strong>More Visuals</strong>
          <small>Find new photos and wall art.</small>
        </Link>
      </section>

      <section className="account-panel" style={{ maxWidth: "1540px", margin: "24px auto 0" }}>
        <div className="account-panel-heading">
          <p className="account-eyebrow">Purchased Files</p>
          <h2>Your digital download library</h2>
          <p>
            Download links will appear here after your account is connected with
            completed orders and secure download tokens.
          </p>
        </div>

        {downloadsError && <div className="account-alert error">{downloadsError}</div>}

        {downloadsLoading ? (
          <div className="account-alert success">Loading downloads...</div>
        ) : downloads.length > 0 ? (
          <div style={{ display: "grid", gap: "14px", marginTop: "22px" }}>
            {downloads.map((download) => (
              <article
                key={download.id || download.token || download.product_slug}
                className="account-side-box"
              >
                <p className="account-eyebrow">
                  {download.license_type || download.licenseType || "Digital Download"}
                </p>

                <h3>{download.product_title || download.title || "Purchased Visual"}</h3>

                <p>
                  {download.description ||
                    "Your purchased file is ready when the secure download route is connected."}
                </p>

                <ul className="account-status-list">
                  <li>
                    <span>Status</span>
                    <strong>{download.status || "Available"}</strong>
                  </li>
                  <li>
                    <span>File type</span>
                    <strong>{download.file_type || download.fileType || "JPG / ZIP"}</strong>
                  </li>
                  <li>
                    <span>Order</span>
                    <strong>{download.order_reference || download.orderId || "N/A"}</strong>
                  </li>
                </ul>

                {download.download_url || download.downloadUrl ? (
                  <a
                    href={download.download_url || download.downloadUrl}
                    className="account-contact-link"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Download File →
                  </a>
                ) : (
                  <Link to="/contact" className="account-contact-link">
                    Need Help? Contact Support →
                  </Link>
                )}
              </article>
            ))}
          </div>
        ) : (
          <div className="account-loading-card" style={{ marginTop: "22px" }}>
            <p className="account-eyebrow">No Downloads Yet</p>
            <h1>Your download library is empty.</h1>
            <p>
              After you buy a photo, poster, or license, your secure download
              access will appear here.
            </p>

            <div className="brand-actions" style={{ marginTop: "22px" }}>
              <Link to="/shop?brand=mayachitro" className="btn btn-gold">
                Shop Photography
              </Link>
            </div>
          </div>
        )}
      </section>
    </main>
  );
}
